import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
	getAuthConnectLoader,
	getAuthRefreshToken,
	getSavedUrl,
} from 'redux/reducers/auth/selectors';
import {
	getOrganizationsRequest,
	getUserInfoRequest,
	setOrganization,
	getHubspotVisitorTokenRequest,
} from 'redux/reducers/user/reducer';
import { Box, Button, Card, CardContent, CardMedia, Typography } from '@mui/material';
import {
	getUserInfo,
	getUserInfoLoader,
	getUserOrganizations,
	getUserOrganizationsLoader,
} from 'redux/reducers/user/selectors';
import { EASSpinner } from 'components/EASSpinner';
import { getMerchantIdSuccess } from 'redux/reducers/auth/reducer';
import { nanoid } from '@reduxjs/toolkit';
import { AnimatedBackground } from 'ui/AnimatedBackground';
import { PATHS } from 'routes/constants';

export const SignIn: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const codeIsLoading = useSelector(getAuthConnectLoader);
	const userInfo = useSelector(getUserInfo);
	const userInfoLoading = useSelector(getUserInfoLoader);
	const organizations = useSelector(getUserOrganizations);
	const organizationsLoading = useSelector(getUserOrganizationsLoader);
	const refreshToken = useSelector(getAuthRefreshToken);

	const savedUrl = useSelector(getSavedUrl);
	// console.log('savedUrl', savedUrl);

	// console.log('SignIn component state:', {
	// 	codeIsLoading,
	// 	userInfoLoading,
	// 	organizationsLoading,
	// 	userInfo: !!userInfo,
	// 	organizations: !!organizations,
	// 	refreshToken: !!refreshToken,
	// });

	useEffect(() => {
		// console.log('SignIn useEffect triggered');
		dispatch(getUserInfoRequest());
		dispatch(getOrganizationsRequest());
	}, [dispatch, codeIsLoading, refreshToken]);

	const handleButtonClick = (em: number) => {
		if (!em || !organizations?.data[em - 1]?.groupId) return;
		dispatch(getMerchantIdSuccess({ xMerchantId: organizations?.data[em - 1]?.groupId }));
		dispatch(setOrganization(organizations?.data[em - 1]));
		// dispatch(getHubspotVisitorTokenRequest());
		// document
		// 	.getElementById('hubspot-messages-iframe-container')
		// 	?.style.setProperty('display', 'block', 'important');
		navigate(
			`${String(!savedUrl || savedUrl === PATHS.SIGN_IN_PAGE || savedUrl === PATHS.INACTIVITY_PAGE ? PATHS.DASHBOARD_PAGE : savedUrl)}`,
		);
	};

	if (codeIsLoading || userInfoLoading || organizationsLoading) return <EASSpinner />;

	if (!userInfo && !userInfoLoading) {
		console.error('UserInfo is null and not loading');
		return (
			<AnimatedBackground>
				<Card
					sx={{
						width: { xs: '100%', sm: 400 },
						height: { xs: '100vh', sm: 'auto' },
						textAlign: 'center',
						mt: { sm: '10vh', xs: 0 },
						color: '#016193',
						position: 'relative',
						zIndex: 1000,
						'&.MuiCard-root': {
							borderRadius: { sm: '12px!important', xs: '0!important' },
						},
					}}
				>
					<CardContent>
						<Typography gutterBottom variant="h5" component="h1" color="error">
							Loading Error
						</Typography>
						<Typography gutterBottom variant="body1" component="div" color="error">
							Failed to load user information
						</Typography>
						<Button
							variant="contained"
							onClick={() => {
								dispatch(getUserInfoRequest());
								dispatch(getOrganizationsRequest());
							}}
							sx={{ mt: 2 }}
						>
							Retry
						</Button>
					</CardContent>
				</Card>
			</AnimatedBackground>
		);
	}

	return (
		<AnimatedBackground>
			<Card
				sx={{
					width: { xs: '100%', sm: 400 },
					height: { xs: '100vh', sm: 'auto' },
					textAlign: 'center',
					mt: { sm: '10vh', xs: 0 },
					color: '#016193',
					position: 'relative',
					zIndex: 1000,
					'&.MuiCard-root': {
						borderRadius: { sm: '12px!important', xs: '0!important' },
					},
				}}
			>
				<CardMedia sx={{ height: 160 }} image="/img/fon.png" />
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						maxWidth: 300,
						padding: '7px 130px',
					}}
				>
					<img
						src="/img/EAS-logo-invert.svg"
						alt=""
						style={{
							width: '100%',
						}}
					/>
				</div>
				<CardContent>
					<Typography gutterBottom variant="h5" component="h1">
						Welcome
					</Typography>
					<Typography gutterBottom variant="h4" component="h2">
						{userInfo?.name}
					</Typography>
					<hr style={{ border: '.2px solid  #016193', marginTop: '20px' }} />
					{(organizations?.data?.length ?? 0) > 1 && (
						<Typography gutterBottom variant="subtitle2" component="div">
							Select the merchant interface to continue
						</Typography>
					)}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignContent: 'start',
							justifyContent: 'start',
							textAlign: 'left',
							padding: '0 16px',
							marginTop: '10px',
							// gap: 1,
						}}
					>
						{(organizations?.data?.length ?? 0) > 1 &&
							organizations?.data?.map((item, index) => (
								<Button
									sx={{
										textAlign: 'left',
										alignContent: 'start',
										justifyContent: 'start',
										textTransform: 'none',
										width: '100%',
										fontWeight: 'bold',
										borderRadius: '12px',
										// border: '1px solid #016193',
										// padding: '5px 5px',
									}}
									key={nanoid()}
									onClick={() => handleButtonClick(index + 1)}
								>
									{item.groupName}
								</Button>
							))}
						{organizations?.data?.length === 1 && (
							<Button
								variant="contained"
								sx={{
									alignContent: 'center',
									justifyContent: 'center',
									textTransform: 'none',
									width: '100%',
									fontWeight: 'bold',
									borderRadius: '12px',
									fontSize: '1.4rem',
								}}
								onClick={() => handleButtonClick(1)}
							>
								Start
							</Button>
						)}
						{organizations?.data?.length === 0 && (
							<Typography gutterBottom variant="subtitle2" component="div">
								No organizations found
							</Typography>
						)}
					</Box>
				</CardContent>
			</Card>
		</AnimatedBackground>
	);
};
